/**
 * Markets Page - single entry point for market data with ONE flat tab strip:
 *   Records / Trends / Comparison (LocalMarkets sections) and Map (MarketMap).
 * The old design stacked a second tab bar inside the Records tab; the shell
 * now owns all four views via ?tab= routing (legacy redirects still land on
 * ?tab=records / ?tab=map).
 */

import React from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { PageHeader } from '../../components/common';
import PlanBanner from '../../components/PlanBanner';
import { useRecipes } from '../../theme/recipes';
import LocalMarkets from '../local-markets/LocalMarkets';
import MarketMap from '../market-map/MarketMap';

const TABS = [
  { value: 'records', label: 'Price Records', icon: 'solar:list-outline' },
  { value: 'trends', label: 'Price Trends', icon: 'solar:chart-2-outline' },
  { value: 'comparison', label: 'Market Comparison', icon: 'solar:align-bottom-outline' },
  { value: 'map', label: 'Map', icon: 'solar:map-point-outline' },
];

function Markets() {
  const recipes = useRecipes();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const activeTab = TABS.some((t) => t.value === tabFromUrl) ? tabFromUrl : 'records';

  const handleTabChange = (_, value) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Analytics"
        title="Markets"
        subtitle="Browse Nigerian commodity prices as records, trends, comparisons, or on a map"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Markets' },
        ]}
      />

      <PlanBanner adds={['export', 'unlimited alerts and holdings']} />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider', ...recipes.tabsSx }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.value}
            value={t.value}
            label={t.label}
            icon={<Icon icon={t.icon} width={18} height={18} />}
            iconPosition="start"
            sx={{ textTransform: 'none', minHeight: 44, fontWeight: 600 }}
          />
        ))}
      </Tabs>

      {activeTab === 'records' && <LocalMarkets embedded view="records" />}
      {activeTab === 'trends' && <LocalMarkets embedded view="trends" />}
      {activeTab === 'comparison' && <LocalMarkets embedded view="comparison" />}
      {activeTab === 'map' && <MarketMap embedded />}
    </Box>
  );
}

export default Markets;
